import store from '../store';
// src/services/bonlivLockService.js
import { BehaviorSubject, Subject, interval, of, throwError, from } from 'rxjs';
import { map, tap, catchError, switchMap, takeUntil, finalize } from 'rxjs/operators';
import axios from 'axios';
import io from 'socket.io-client';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/bl`;
console.log('BonlivLockService initialized with API_URL:', API_URL);

// Add tracking for the current view/state
let isInDetailView = false;

class BonlivLockService {
  constructor() {
    // Store currently locked bonlivs
    this.lockedBonlivs = new BehaviorSubject({});
    
    // Store the bonliv ID currently locked by this user
    this.currentUserLock = new BehaviorSubject(null);
    
    // For cleanup/releasing locks
    this.destroy$ = new Subject();
    
    // User info (should be set from your auth system)
    this.currentUser = {
      id: null,
      name: null
    };
    
    // Flag to prevent multiple fetches at once
    this._isFetching = false;
    
    // Track last navigation path to avoid false triggers
    this.lastPath = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Heartbeat to maintain locks (every 30 seconds)
    this.heartbeat$ = interval(30000).pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        const currentLockId = this.currentUserLock.getValue();
        if (currentLockId) {
          return this.refreshLock(currentLockId);
        }
        return of(null);
      })
    ).subscribe();
    
    // Subscribe to server-side lock events (could use WebSockets)
    this.initLockListener();
  }
  
  setCurrentUser(user) {
    this.currentUser = user;
    if (user?.id && this.socket && !this.socket.connected) this.socket.connect();
  }
  
  // New method to fetch locks just once - use this instead of syncLocksWithServer
  fetchLocks() {
    // Prevent multiple concurrent fetches
    if (this._isFetching) {
      console.log('Fetch already in progress, skipping');
      return;
    }
    
    this._isFetching = true;
    console.log('Fetching locks once');
    
    // Make API call to get locks
    from(axios.get(`${API_URL}/lock`))
      .pipe(
        map(response => response.data || {}),
        catchError(error => {
          console.error('Error fetching locks:', error);
          return of({});
        }),
        finalize(() => {
          this._isFetching = false;
        })
      )
      .subscribe(locks => {
        console.log('Received lock data from API:', locks);
        this.lockedBonlivs.next(locks);
      });
  }
  
  // New method to mark entering detail view
  enterDetailView() {
    console.log('Entering bonliv detail view');
    isInDetailView = true;
  }
  
  // New method to mark exiting detail view
  exitDetailView() {
    console.log('Exiting bonliv detail view');
    isInDetailView = false;
    // Release lock when explicitly exiting
    const currentLockId = this.currentUserLock.getValue();
    if (currentLockId) {
      this.releaseLock(currentLockId).subscribe({
        next: () => console.log('Lock released when exiting detail view'),
        error: (err) => console.error('Failed to release lock when exiting detail view:', err)
      });
    }
  }
  
  // Initialize listener for lock changes (using WebSockets)
  initLockListener() {
    try {
      // Use WebSockets if possible
      const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
      console.log('Connecting to WebSocket server at:', socketUrl);
      
      const socket = io(socketUrl, { auth: callback => callback({ token: store.getState().token }) });
      this.socket = socket;
      
      socket.on('connect', () => {
        console.log('WebSocket connected for lock notifications');
      });
      
      socket.on('disconnect', () => {
        console.log('WebSocket disconnected for lock notifications');
      });
      
      socket.on('bonliv-lock-changed', (lockEvent) => {
        console.log('Received lock change event:', lockEvent);
        
        // Update our local lock status
        const currentLocks = this.lockedBonlivs.getValue();
        
        if (lockEvent.isLocked) {
          // Someone acquired a lock
          this.lockedBonlivs.next({
            ...currentLocks,
            [lockEvent.bonlivId]: {
              bonlivId: lockEvent.bonlivId,
              userId: lockEvent.userId,
              userName: lockEvent.userName,
              timestamp: new Date().toISOString()
            }
          });
        } else {
          // Someone released a lock
          const { [lockEvent.bonlivId]: _, ...remainingLocks } = currentLocks;
          this.lockedBonlivs.next(remainingLocks);
          
          // Check if this was a force-unlock by an admin
          if (lockEvent.forceUnlockedBy && lockEvent.previousOwner === this.currentUser.id) {
            // Show notification that admin force-unlocked your document
            try {
              // Try to dynamically import antd
              const antd = require('antd');
              if (antd && antd.message) {
                antd.message.warning('Un administrateur a déverrouillé votre document.');
              } else {
                console.warn('Your document was force-unlocked by an administrator');
              }
            } catch (e) {
              console.warn('Your document was force-unlocked by an administrator');
            }
            
            // If this was the current user's lock, clear it
            if (this.currentUserLock.getValue() === lockEvent.bonlivId) {
              this.currentUserLock.next(null);
              isInDetailView = false;
            }
          }
        }
      });
    } catch (error) {
      console.error('Error initializing WebSocket for lock notifications:', error);
    }
  }
  
  // Check if navigation is significant (changing pages, not just params)
  isSignificantNavigation(newPath) {
    // If we're in detail view, any navigation is significant
    if (isInDetailView) {
      const pathParts = this.lastPath.split('/');
      const newPathParts = newPath.split('/');
      
      // If base paths differ (e.g., "/bonliv/123" -> "/bonlivs"), it's significant
      if (pathParts[1] !== newPathParts[1]) {
        return true;
      }
      
      // If ID changes (e.g., "/bonliv/123" -> "/bonliv/456"), it's significant
      if (pathParts[2] !== newPathParts[2]) {
        return true;
      }
    }
    return false;
  }
  
  // Handle page unload events (browser close, refresh, etc.)
  handlePageUnload() {
    const currentLockId = this.currentUserLock.getValue();
    if (currentLockId && isInDetailView) {
      // Using synchronous XMLHttpRequest for better reliability during page unload
      const xhr = new XMLHttpRequest();
      xhr.open('DELETE', `${API_URL}/lock/${currentLockId}?userId=${this.currentUser.id}`, false);
      xhr.setRequestHeader('Content-Type', 'application/json');
      try {
        xhr.send();
        console.log('Lock released on page unload');
      } catch (e) {
        console.error('Failed to release lock on unload:', e);
      }
    }
  }
  
  // Handle in-app navigation
  handleNavigation(newPath) {
    const currentLockId = this.currentUserLock.getValue();
    // Only release lock if we're actually leaving the bonliv detail
    if (currentLockId && isInDetailView) {
      console.log('Navigation detected, releasing lock:', currentLockId);
      this.releaseLock(currentLockId).subscribe({
        next: () => console.log('Lock released due to navigation'),
        error: (err) => console.error('Failed to release lock on navigation:', err)
      });
      isInDetailView = false;
    }
  }
  
  // Acquire a lock on a bonliv
  acquireLock(bonlivId) {
    // Check if already locked by someone else
    const currentLocks = this.lockedBonlivs.getValue();
    
    if (currentLocks[bonlivId] && 
        currentLocks[bonlivId].userId !== this.currentUser.id) {
      return throwError(() => ({
        message: `Ce document est actuellement utilisé par ${currentLocks[bonlivId].userName}`,
        lockedBy: currentLocks[bonlivId]
      }));
    }
    
    // If we already have the lock, just return it
    if (this.currentUserLock.getValue() === bonlivId) {
      return of({
        bonlivId,
        success: true,
        message: 'Vous avez déjà accès à ce document'
      });
    }
    
    // Otherwise try to acquire the lock from the server
    return this.apiAcquireLock(bonlivId).pipe(
      tap(response => {
        if (response.success) {
          // Update local state
          const updatedLocks = {
            ...currentLocks,
            [bonlivId]: {
              bonlivId,
              userId: this.currentUser.id,
              userName: this.currentUser.name,
              timestamp: new Date().toISOString()
            }
          };
          
          this.lockedBonlivs.next(updatedLocks);
          this.currentUserLock.next(bonlivId);
          
          // Don't set detail view here - that should be done explicitly by enterDetailView
          // isInDetailView = true;
        }
      }),
      catchError(error => {
        console.error('Error acquiring lock:', error);
        return throwError(() => ({
          message: 'Impossible de verrouiller ce document. Veuillez réessayer.',
          error
        }));
      })
    );
  }
  
  // Release a lock on a bonliv
  releaseLock(bonlivId) {
    const currentLocks = this.lockedBonlivs.getValue();
    
    // Only the lock owner can release it
    if (currentLocks[bonlivId]?.userId !== this.currentUser.id) {
      return of({ success: false, message: 'Vous ne possédez pas ce verrou' });
    }
    
    return this.apiReleaseLock(bonlivId).pipe(
      tap(response => {
        if (response.success) {
          // Update local state immediately without waiting for server
          // This helps prevent any potential race conditions
          console.log("RELEASE API");
          const { [bonlivId]: _, ...remainingLocks } = currentLocks;
          this.lockedBonlivs.next(remainingLocks);
          
          if (this.currentUserLock.getValue() === bonlivId) {
            this.currentUserLock.next(null);
            // Also mark that we're no longer in detail view
            isInDetailView = false;
          }
        }
      }),
      catchError(error => {
        console.error('Error releasing lock:', error);
        return throwError(() => ({
          message: 'Impossible de déverrouiller ce document. Veuillez réessayer.',
          error
        }));
      })
    );
  }
  
  // Refresh a lock to keep it active
  refreshLock(bonlivId) {
    return this.apiRefreshLock(bonlivId).pipe(
      catchError(error => {
        console.error('Error refreshing lock:', error);
        // If refresh fails, we might have lost the lock
        this.checkLockStatus(bonlivId);
        return of(null);
      })
    );
  }
  
  // Check if we still have the lock
  checkLockStatus(bonlivId) {
    return this.apiCheckLock(bonlivId).pipe(
      tap(status => {
        if (!status.isLocked || status.userId !== this.currentUser.id) {
          // We lost the lock
          const currentLocks = this.lockedBonlivs.getValue();
          const { [bonlivId]: _, ...remainingLocks } = currentLocks;
          this.lockedBonlivs.next(remainingLocks);
          
          if (this.currentUserLock.getValue() === bonlivId) {
            this.currentUserLock.next(null);
          }
        }
      })
    );
  }
  
  // Check if a bonliv is locked - FIXED to check any lock regardless of user
  isLocked(bonlivId) {
    const currentLocks = this.lockedBonlivs.getValue();
    return Boolean(currentLocks[bonlivId]);
  }
  
  // Optional: Add a method to check if it's locked by the current user
  isLockedByCurrentUser(bonlivId) {
    const currentLocks = this.lockedBonlivs.getValue();
    return Boolean(currentLocks[bonlivId] && 
                  currentLocks[bonlivId].userId === this.currentUser.id);
  }
  
  // Get information about who has locked a bonliv
  getLockInfo(bonlivId) {
    const currentLocks = this.lockedBonlivs.getValue();
    return currentLocks[bonlivId] || null;
  }
  
  // Admin function to force-unlock a bonliv
  forceUnlock(bonlivId) {
    return this.apiForceUnlock(bonlivId).pipe(
      tap(response => {
        if (response.success) {
          // Update local state
          const currentLocks = this.lockedBonlivs.getValue();
          const { [bonlivId]: _, ...remainingLocks } = currentLocks;
          this.lockedBonlivs.next(remainingLocks);
          
          // If this was the current user's lock, clear it
          if (this.currentUserLock.getValue() === bonlivId) {
            this.currentUserLock.next(null);
            isInDetailView = false;
          }
        }
      }),
      catchError(error => {
        console.error('Error force-unlocking:', error);
        return throwError(() => ({
          message: 'Impossible de déverrouiller ce document. Action réservée aux administrateurs.',
          error
        }));
      })
    );
  }
  
  // Get observable of all locked bonlivs
  getLockedBonlivs() {
    return this.lockedBonlivs.asObservable();
  }
  
  // Get observable for the current user's lock
  getCurrentUserLock() {
    return this.currentUserLock.asObservable();
  }
  
  // API calls to backend
  apiAcquireLock(bonlivId) {
    // Check if API_URL is properly set
    if (!process.env.REACT_APP_API_BASE_URL) {
      console.error('REACT_APP_API_BASE_URL is not set!');
      return throwError(() => new Error('API URL is not configured properly. Please check environment variables.'));
    }
    
    const url = `${API_URL}/lock`;
    console.log(`Attempting to acquire lock for bonliv ${bonlivId} at URL: ${url}`);
    
    // Using axios for API calls
    return from(axios.post(url, { 
      bonlivId, 
      userId: this.currentUser.id,
      userName: this.currentUser.name 
    })).pipe(
      tap(response => console.log('Lock acquisition response:', response.data)),
      map(response => response.data),
      catchError(error => {
        console.error('Error in API call to acquire lock:', error);
        return throwError(() => ({
          message: `Failed to acquire lock: ${error.message}`,
          error
        }));
      })
    );
  }
  
  apiReleaseLock(bonlivId) {
    return from(axios.delete(`${API_URL}/lock/${bonlivId}`, {
      params: { userId: this.currentUser.id }
    })).pipe(
      map(response => response.data)
    );
  }
  
  apiRefreshLock(bonlivId) {
    return from(axios.put(`${API_URL}/lock/${bonlivId}/refresh`, {
      userId: this.currentUser.id
    })).pipe(
      map(response => response.data)
    );
  }
  
  apiCheckLock(bonlivId) {
    return from(axios.get(`${API_URL}/lock/${bonlivId}`)).pipe(
      map(response => response.data)
    );
  }
  
  apiForceUnlock(bonlivId) {
    return from(axios.delete(`${API_URL}/lock/${bonlivId}/force`, {
      params: { 
        userId: this.currentUser.id,
        isAdmin: true 
      }
    })).pipe(
      map(response => response.data)
    );
  }
  
  // Deprecated - use fetchLocks() instead
  syncLocksWithServer() {
    console.warn('syncLocksWithServer is deprecated, use fetchLocks() instead');
    this.fetchLocks();
    return of({ success: true });
  }
  
  // Clean up any stale locks for the current user
  cleanupUserLocks() {
    if (!this.currentUser.id) {
      console.warn('Cannot cleanup locks: No user ID set');
      return of({ success: false, message: 'No user ID set' });
    }
    
    // Don't delete current active lock
    const currentLockId = this.currentUserLock.getValue();
    
    // Only clean up if we have stale locks (not our current lock)
    const currentLocks = this.lockedBonlivs.getValue();
    const userLocks = Object.keys(currentLocks).filter(
      bonlivId => currentLocks[bonlivId].userId === this.currentUser.id && bonlivId !== currentLockId
    );
    
    if (userLocks.length === 0) {
      console.log('No stale locks to clean up');
      return of({ success: true, message: 'No stale locks found' });
    }
    
    console.log('Cleaning up stale locks for user, preserving current lock:', currentLockId);
    
    // Manually clean up each lock except the current one
    return from(axios.delete(`${API_URL}/lock/user/${this.currentUser.id}`, {
      params: { preserveLockId: currentLockId }
    })).pipe(
      tap(response => {
        console.log('User locks cleanup response:', response.data);
        if (response.data.success) {
          // Refresh our locks data
          this.fetchLocks();
        }
      }),
      catchError(error => {
        console.error('Error cleaning up user locks:', error);
        return of({ success: false, error });
      })
    );
  }
  
  // Cleanup when service is destroyed
  destroy() {
    // Clean up RxJS subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    // Disconnect WebSocket
    if (this.socket) {
      this.socket.disconnect();
    }
    
    // Release any locks held by this user
    const currentLockId = this.currentUserLock.getValue();
    if (currentLockId) {
      this.releaseLock(currentLockId).subscribe();
    }
  }
}

// Create a singleton instance
const bonlivLockService = new BonlivLockService();
export default bonlivLockService;